<!-- =========================================================================================
	File Name: VxSidebarGroup.vue
	Description: Sidebar Group Component. Extends vuesax framework's 'vs-sidebar-group' component
	Component Name: VxSidebarGroup
	----------------------------------------------------------------------------------------
	Item Name: Vuesax Admin - VueJS Dashboard Admin Template
	Author: Pixinvent
	Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>
  <div
    :class="[
      { 'vs-sidebar-group-open': openItems },
      { 'vs-sidebar-group-active': open },
      { 'disabled-item pointer-events-none': group.isDisabled },
    ]"
    class="vs-sidebar-group"
    @mouseover="mouseover"
    @mouseout="mouseout"
    v-if="canSubMenu()"
  >
    <div @click="clickGroup" class="group-header w-full">
      <span class="flex items-center w-full">
        <feather-icon
          :icon="group.icon || 'CircleIcon'"
          :svgClasses="{ 'w-3 h-3': this.groupIndex % 1 != 0 }"
          v-if="group.icon || this.groupIndex > Math.floor(this.groupIndex)"
        />
        <span v-show="!sidebarItemsMin" class="truncate mr-3 select-none">{{
          group.label || group.name
        }}</span>
        <span
          class="badge"
          v-if="group.hasBadge == true && countBadgeNewItems != null"
          >{{ countBadgeNewItems[group.keyBadge] }}</span
        >
        <vs-chip
          class="ml-auto mr-4"
          :color="group.tagColor"
          v-if="group.tag && !sidebarItemsMin"
          >{{ group.tag }}</vs-chip
        >
      </span>
      <feather-icon
        icon="ChevronRightIcon"
        svg-classes="w-4 h-4"
        :class="[{ rotate90: openItems }, 'feather-grp-header-arrow']"
        v-show="!sidebarItemsMin"
      />
      <span class="vs-sidebar--tooltip">{{ group.name }}</span>
    </div>
    <ul ref="items" :style="styleItems" class="vs-sidebar-group-items">
      <li v-for="(groupItem, index) in group.submenu" :key="index">
        <vx-sidebar-group
          :group="groupItem"
          :groupIndex="Number(`${groupIndex}.${index}`)"
          :open="isGroupActive(groupItem)"
          :openHover="openHover"
          v-if="groupItem.submenu"
        />
        <div v-else>
          <vx-sidebar-item
            v-if="
              groupItem.permission
                ? $can(groupItem.permission, groupItem.name)
                : hasReadPermission(groupItem)
            "
            :index="groupIndex + '.' + index"
            :icon="itemIcon(groupIndex + '.' + index)"
            icon-small
            :target="groupItem.target"
            :to="groupItem.slug != 'external' ? groupItem.url : ''"
            :href="groupItem.slug == 'external' ? groupItem.url : ''"
          >
            <span class="truncate">{{
              groupItem.label || groupItem.name
            }}</span>
            <vs-chip
              class="ml-auto"
              :color="groupItem.tagColor"
              v-if="groupItem.tag"
              >{{ groupItem.tag }}</vs-chip
            >
          </vx-sidebar-item>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import { READ_PERMISSIONS } from '../../../constants/router.permissions';
import VxSidebarItem from './VxSidebarItem.vue';

export default {
  name: 'vx-sidebar-group',
  props: {
    openHover: {
      default: false,
      type: Boolean,
    },
    open: {
      default: false,
      type: Boolean,
    },
    group: {
      type: Object,
    },
    groupIndex: {
      type: Number,
    },
  },
  data: () => ({
    maxHeight: '0px',
    openItems: false,
  }),
  computed: {
    sidebarItemsMin() {
      return this.$store.state.sidebarItemsMin;
    },
    styleItems() {
      return {
        maxHeight: this.maxHeight,
      };
    },
    itemIcon() {
      return (index) => {
        if (!((index.match(/\./g) || []).length > 1)) return 'CircleIcon';
      };
    },
    isGroupActive() {
      return (sidebarItem) => {
        const path = this.$route.fullPath;
        let open = false;
        let func = function (sidebarItem) {
          if (sidebarItem.submenu) {
            sidebarItem.submenu.forEach((item) => {
              if (path == item.url) {
                open = true;
              } else if (item.submenu) {
                func(item);
              }
            });
          }
        };

        func(sidebarItem);
        return open;
      };
    },
    countBadgeNewItems: {
      get() {
        return this.$store.state.countBadgeNewItems;
      },
    },
  },
  watch: {
    // OPEN & CLOSES DROPDOWN ON ROUTE CHANGE
    $route() {
      if (this.sidebarItemsMin) return;
      let scrollHeight = this.scrollHeight ? this.scrollHeight : 0;
      if (this.openItems && !this.open) {
        this.maxHeight = `${scrollHeight}px`;
        setTimeout(() => {
          this.maxHeight = `${0}px`;
        }, 50);
      } else if (this.open) {
        this.maxHeight = `${scrollHeight}px`;
        setTimeout(() => {
          this.maxHeight = 'none';
        }, 300);
      }
    },
    maxHeight() {
      this.openItems = this.maxHeight != '0px';
    },
    // OPEN AND CLOSES DROPDOWN MENU ON SIDEBAR COLLAPSE AND DEFAULT VIEW
    '$store.state.sidebarItemsMin'(val) {
      let scrollHeight = this.$refs.items ? this.$refs.items.scrollHeight : 0;
      if (!val && this.open) {
        this.maxHeight = `${scrollHeight}px`;
        setTimeout(() => {
          this.maxHeight = 'none';
        }, 300);
      } else {
        this.maxHeight = `${scrollHeight}px`;
        setTimeout(() => {
          this.maxHeight = '0px';
        }, 50);
      }
      if (val && this.open) {
        this.maxHeight = `${scrollHeight}px`;
        setTimeout(() => {
          this.maxHeight = '0px';
        }, 250);
      }
    },
  },
  methods: {
    hasReadPermission(item) {
      return READ_PERMISSIONS.some((permission) =>
        this.$can(permission, item.name),
      );
    },
    clickGroup() {
      if (!this.openHover) {
        let thisScrollHeight = this.$refs.items
          ? this.$refs.items.scrollHeight
          : 0;
        if (this.maxHeight == '0px') {
          this.maxHeight = `${thisScrollHeight}px`;
          setTimeout(() => {
            this.maxHeight = 'none';
          }, 300);
        } else {
          this.maxHeight = `${thisScrollHeight}px`;
          setTimeout(() => {
            this.maxHeight = `${0}px`;
          }, 50);
        }

        this.$parent.$children.map((child) => {
          if (child.isGroupActive) {
            if (child !== this && !child.open && child.maxHeight != '0px') {
              setTimeout(() => {
                child.maxHeight = `${0}px`;
              }, 50);
            }
          }
        });
      }
    },
    mouseover() {
      if (this.openHover) {
        let scrollHeight = this.$refs.items ? this.$refs.items.scrollHeight : 0;
        this.maxHeight = `${scrollHeight}px`;
      }
    },
    mouseout() {
      if (this.openHover) {
        let scrollHeight = 0;
        this.maxHeight = `${scrollHeight}px`;
      }
    },
    canSubMenu(currentSubmenu = this.group.submenu) {
      const submenu = currentSubmenu;
      if (submenu) {
        for (let index = 0; index < submenu.length; index++) {
          const element = submenu[index];
          const hasPermission = element.permission
            ? this.$can(element.permission, element.name)
            : this.hasReadPermission(element);
          if (hasPermission) {
            return true;
          }
          if (element.submenu && this.canSubMenu(element.submenu)) {
            return true;
          }
        }
      }
      return false;
    },
  },
  components: {
    VxSidebarItem,
  },
  mounted() {
    this.openItems = this.open;
    if (this.open) {
      this.maxHeight = 'none';
    }
  },
};
</script>

<style lang="scss">
@import '@/assets/scss/vuesax/components/vxSidebarGroup.scss';
</style>
